import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import { HiOutlineCalendar, HiOutlineCamera } from "react-icons/hi2";
import { API_URL } from "../../config";

type JwtPayload = {
    userId: number;
    username: string;
    role: string;
};

type RoleData = {
    role_id: number;
    role_name: string;
    role: string;
    is_active: boolean;
    user_name_surname: string;
    login: string;
    password: string;
    producer_or_producer_group_id: number;
    relation_type_id: number;
    user_id: number;
    created_timestamp: string;
    mail: string;
    phone: number;
};

interface AccountInfoProps {
    setFetchUsersCallback: (callback: () => void) => void;
}

const getInitials = (name?: string) => {
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatRomanianDate = (dateStr?: string) => {
    if (!dateStr) return "20 decembrie 2024";
    const months = [
        "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
        "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
    ];
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
};

export const AccountInfo: React.FC<AccountInfoProps> = ({ setFetchUsersCallback }) => {
    const location = useLocation();
    const { item: initialData } = location.state || {};
    const [selectedEditRowData, setSelectedEditRowData] = useState<RoleData | null>(initialData || null);
    const [create, setCreate] = useState(initialData?.created_timestamp || "");
    const [showGrid, setShowGrid] = useState(true);

    const token = localStorage.getItem('jwtToken');

    const fetchUsers = async () => {
        try {
            let targetUserId = initialData?.user_id;
            if (!targetUserId && token) {
                try {
                    const decoded: JwtPayload = jwtDecode(token);
                    targetUserId = decoded.userId;
                } catch (error) {
                    console.error('Error decoding the token:', error);
                }
            }

            if (!targetUserId) return;

            const response = await axios.get(`${API_URL}/api/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const matchedUser = response.data.find((item: any) => item.user_id === targetUserId);
            if (matchedUser) {
                setSelectedEditRowData(matchedUser);
                setCreate(matchedUser.created_timestamp);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [initialData]);

    useEffect(() => {
        setFetchUsersCallback(() => fetchUsers);
    }, [setFetchUsersCallback]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowGrid(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const displayName = selectedEditRowData?.user_name_surname || selectedEditRowData?.login || "Utilizator";
    const displayRole = selectedEditRowData?.role_name || (selectedEditRowData?.role_id === 1 ? "Admin" : selectedEditRowData?.role_id === 2 ? "Manager" : "User") || "User";
    const isActive = selectedEditRowData?.is_active !== undefined ? Boolean(selectedEditRowData.is_active) : true;
    const memberSinceFormatted = formatRomanianDate(selectedEditRowData?.created_timestamp || create);
    const initials = getInitials(displayName);

    return (
        <div className="w-full h-full">
            {showGrid ? (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs animate-pulse flex flex-col justify-between h-full min-h-[300px]">
                    <div className="flex justify-end">
                        <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="w-28 h-28 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-36 h-5 bg-gray-200 dark:bg-gray-700 rounded-md mt-4"></div>
                    </div>
                    <div className="w-full h-14 bg-gray-100 dark:bg-gray-700/50 rounded-xl mt-6"></div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs relative flex flex-col justify-between h-full min-h-[300px] transition-all duration-200">
                    {/* Role Badge Top-Right */}
                    <div className="absolute top-6 right-6">
                        <span className="px-3 py-1 rounded-lg text-xs font-semibold bg-[#ECE8FF] text-[#6755E8] dark:bg-indigo-950/70 dark:text-indigo-300">
                            {displayRole}
                        </span>
                    </div>

                    {/* Center: Large Soft Rounded Avatar & Name */}
                    <div className="flex flex-col items-center pt-2">
                        <div className="w-[112px] h-[112px] rounded-full p-2 bg-[#F8FAFC] dark:bg-gray-700/30 flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-[#ECE8FF] dark:bg-indigo-950/70 text-[#6755E8] dark:text-indigo-300 font-bold text-3xl flex items-center justify-center tracking-tight">
                                {initials}
                            </div>
                        </div>

                        <h2 className="text-[17px] font-bold text-[#1E293B] dark:text-gray-100 mt-4 text-center tracking-tight">
                            {displayName}
                        </h2>
                    </div>

                    {/* Bottom Meta Box: Membru din & Cont activ */}
                    <div className="mt-6 p-3 bg-white dark:bg-gray-800/80 border border-[#E2E8F0] dark:border-gray-700 rounded-xl flex items-center justify-between">
                        {/* Left: Membru din */}
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#ECE8FF] dark:bg-indigo-950/60 text-[#6755E8] dark:text-indigo-400 flex items-center justify-center shrink-0">
                                <HiOutlineCalendar className="w-4 h-4" />
                            </div>
                            <div>
                                <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider block leading-none mb-1">
                                    MEMBRU DIN
                                </span>
                                <span className="text-xs font-bold text-[#1E293B] dark:text-gray-200 block leading-none">
                                    {memberSinceFormatted}
                                </span>
                            </div>
                        </div>

                        {/* Right: Cont activ */}
                        <div className="flex items-center gap-2 pl-3 border-l border-gray-100 dark:border-gray-700">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-[#18A957]' : 'bg-rose-500'}`}></span>
                            <div>
                                <span className="text-xs font-bold text-[#1E293B] dark:text-gray-200 block leading-tight">
                                    {isActive ? "Cont activ" : "Cont inactiv"}
                                </span>
                                <span className="text-[10px] text-gray-400 font-normal block leading-tight">
                                    {isActive ? "Acces complet" : "Acces limitat"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
